import entries from 'lodash-es/entries'
import omit from 'lodash-es/omit'

import {
  EMPTY_LIST,
  EMPTY_STRING,
  NOTIFICATION_TYPE,
} from '../../../../constants/common'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_BCC,
  EMAIL_CREATION_FORM_FIELD_NAME_CC,
  EMAIL_CREATION_FORM_FIELD_NAME_DELETE_EMPTY_EMAIL_USERS,
  EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES_AS_LINKS,
  EMAIL_CREATION_FORM_FIELD_NAME_PRINTER,
  EMAIL_CREATION_FORM_FIELD_NAME_PRIORITY,
  EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EMAIL_CREATION_FORM_SEPARATE,
  getEmailFormPayload,
} from '../../../Email'
import {
  DocFlowSendingWidgetNotificationType,
  DocFlowSendingWidgetProcessFieldName,
} from '../../DocFlowSending'

import {
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_AI_DRAFT,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_ERROR,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_PERMANENT,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_RECIPIENTS_IN_BLACK_LIST_ERROR,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_RECIPIENTS_WITHOUT_EMAIL_ERROR,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_REQUIRED_FIELDS,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_SUCCESS,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_UNAVAILABLE,
  DOC_FLOW_SENDING_EMAIL_SETTINGS_SERVER_NON_FIELD_ERRORS,
} from './DocFlowSendingEmailSettingsWidget.constants'

function getDocFlowSendingEmailSettingsPayloadName(name) {
  switch (name) {
    case EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES:
      return DocFlowSendingWidgetProcessFieldName.EmailDocumentTemplates

    case EMAIL_CREATION_FORM_FIELD_NAME_PRINTER:
      return DocFlowSendingWidgetProcessFieldName.EmailPrinterId

    case EMAIL_CREATION_FORM_FIELD_NAME_DELETE_EMPTY_EMAIL_USERS:
      return name

    default:
      return `email_${name}`
  }
}

export function getDocFlowSendingEmailSettingsPermanentNotifications(t) {
  return {
    id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_PERMANENT,
    text: t('DocumentStapling.SendingPhaseEmailWidgetNotificationKey'),
    type: NOTIFICATION_TYPE.info,
  }
}

export function getDocFlowSendingEmailSettingsUnavailableNotification(t) {
  return {
    id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_UNAVAILABLE,
    text: t(
      'DocumentStapling.SendingPhaseEmailWidgetUnavailableNotificationKey'
    ),
    type: NOTIFICATION_TYPE.info,
  }
}

export function getDocFlowSendingEmailSettingsPayload(formikContext) {
  if (formikContext) {
    const filesAsLinks =
      formikContext.values?.[EMAIL_CREATION_FORM_FIELD_NAME_FILES_AS_LINKS] ??
      EMPTY_LIST
    const emailPayload = getEmailFormPayload(formikContext.values)

    const payload = omit(emailPayload, [
      EMAIL_CREATION_FORM_FIELD_NAME_PRIORITY,
      EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO,
      EMAIL_CREATION_FORM_SEPARATE,
    ])

    const mappedPayload = entries(payload).reduce((result, [key, value]) => {
      const name = getDocFlowSendingEmailSettingsPayloadName(key)

      return {
        ...result,
        [name]: value,
      }
    }, {})

    return {
      ...mappedPayload,
      [DocFlowSendingWidgetProcessFieldName.EmailDocumentTemplates]:
        mappedPayload[
          DocFlowSendingWidgetProcessFieldName.EmailDocumentTemplates
        ] ?? EMPTY_LIST,
      [DocFlowSendingWidgetProcessFieldName.EmailFilesAsLinks]:
        filesAsLinks.length > 0,
    }
  }
}

export function getDocFlowSendingEmailSettingsWidgetNotifications(t) {
  return {
    [DocFlowSendingWidgetNotificationType.Error]: {
      id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_ERROR,
      text: t('DocumentStapling.SendingPhaseEmailWidgetErrorNotificationKey'),
      type: NOTIFICATION_TYPE.error,
    },
    [DocFlowSendingWidgetNotificationType.Success]: {
      id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_SUCCESS,
      text: t('DocumentStapling.SendingPhaseEmailWidgetSuccessNotificationKey'),
      type: NOTIFICATION_TYPE.success,
    },
    aiDraftNotApplied: {
      id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_AI_DRAFT,
      text: t('DocFlow.AIDraftNotAppliedErrorKey'),
      type: NOTIFICATION_TYPE.error,
    },
    recipientsInBlackList: {
      id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_RECIPIENTS_IN_BLACK_LIST_ERROR,
      text: t(
        'DocumentStapling.SendingPhaseEmailWidgetRecipientsInBlackListErrorNotificationKey'
      ),
      type: NOTIFICATION_TYPE.error,
    },
    recipientsWithoutEmail: {
      id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_RECIPIENTS_WITHOUT_EMAIL_ERROR,
      text: t(
        'DocumentStapling.SendingPhaseEmailWidgetRecipientsWithoutEmailErrorNotificationKey'
      ),
      type: NOTIFICATION_TYPE.error,
    },
    requiredFields: {
      id: DOC_FLOW_SENDING_EMAIL_SETTINGS_NOTIFICATION_ID_REQUIRED_FIELDS,
      text: t(
        'DocumentStapling.SendingPhaseEmailWidgetRequiredFieldsErrorNotificationKey'
      ),
      type: NOTIFICATION_TYPE.error,
    },
  }
}

export function getBlockedRecipients(items) {
  return items?.reduce((acc, current) => {
    if (current.is_blocked) {
      return acc.concat(current.email)
    }

    return acc
  }, [])
}

export function getDocFlowEmailFlattenRecipients(values) {
  if (values) {
    return [
      ...values[EMAIL_CREATION_FORM_FIELD_NAME_TO],
      ...values[EMAIL_CREATION_FORM_FIELD_NAME_CC],
      ...values[EMAIL_CREATION_FORM_FIELD_NAME_BCC],
    ]
  }

  return EMPTY_LIST
}

export function parseDocFlowEmailSettingsServerError(error, t) {
  const errorText = error?.response?.data?.errors ?? EMPTY_STRING

  if (DOC_FLOW_SENDING_EMAIL_SETTINGS_SERVER_NON_FIELD_ERRORS[errorText]) {
    return t(DOC_FLOW_SENDING_EMAIL_SETTINGS_SERVER_NON_FIELD_ERRORS[errorText])
  }

  return errorText
}
